'use client'
import { createContext, useContext, useEffect, useState, ReactNode } from 'react'
import { useRouter } from 'next/navigation'
import { AuthUser } from '@/types/auth'

interface AuthContextType {
  user: AuthUser | null
  token: string | null
  loading: boolean
  login: (token: string, user: AuthUser) => void
  logout: () => void
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

/**
 * ¿La URL es del backend del CRM? Mismo origen que el API (NEXT_PUBLIC_API_URL,
 * o el propio dominio si no está definido) y ruta que empieza con /api/.
 * Las URL relativas ("/api/...") se resuelven contra el dominio actual.
 */
function isOwnApiUrl(url: string): boolean {
  try {
    const target = new URL(url, window.location.origin)
    const apiBase = new URL(process.env.NEXT_PUBLIC_API_URL || window.location.origin, window.location.origin)
    return target.origin === apiBase.origin && target.pathname.startsWith('/api/')
  } catch {
    return false
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null)
  const [token, setToken] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const router = useRouter()

  useEffect(() => {
    const storedToken = localStorage.getItem('crm_token')
    const storedUser = localStorage.getItem('crm_user')
    if (storedToken && storedUser) {
      setToken(storedToken)
      setUser(JSON.parse(storedUser))
    }
    setLoading(false)
  }, [])

  const login = (newToken: string, newUser: AuthUser) => {
    localStorage.setItem('crm_token', newToken)
    localStorage.setItem('crm_user', JSON.stringify(newUser))
    setToken(newToken)
    setUser(newUser)
  }

  const logout = () => {
    localStorage.removeItem('crm_token')
    localStorage.removeItem('crm_user')
    setToken(null)
    setUser(null)
    router.push('/login')
  }

  // Interceptor global de 401 -- el token dura 6h (ver routes/auth.ts). Sin
  // esto, cuando vence, cada fetch de refresco (polling de conversaciones,
  // contadores, etc.) empieza a fallar en silencio y la pantalla se queda
  // "congelada" con datos viejos hasta que el usuario sale y entra
  // manualmente. Como cada página hace sus propios fetch() sueltos (no hay
  // un cliente HTTP centralizado en toda la app), se parchea window.fetch
  // UNA sola vez aquí para cubrir toda la aplicación sin tocar cada página.
  useEffect(() => {
    const originalFetch = window.fetch.bind(window)

    window.fetch = async (...args: Parameters<typeof fetch>) => {
      const response = await originalFetch(...args)

      if (response.status === 401) {
        const input = args[0]
        const url = typeof input === 'string' ? input
          : input instanceof URL ? input.href
          : (input as Request).url
        // Solo forzamos el logout si la petición era hacia NUESTRO backend
        // (mismo origen que el API del CRM y ruta /api/...) y si de verdad
        // había una sesión activa -- así no se dispara con un 401 de login por
        // credenciales incorrectas (ahí no hay token guardado todavía) ni con
        // llamadas a servicios de terceros que también puedan devolver 401.
        //
        // ANTES se comprobaba solo url.includes('/api/'), y eso también
        // coincidía con servicios externos: el SDK de Wavoip, al entrar una
        // llamada, hace peticiones a SUS servidores (con /api/ en la ruta) y
        // un 401 de ellos cerraba la sesión del CRM.
        if (isOwnApiUrl(url) && localStorage.getItem('crm_token')) {
          console.warn('[auth] Sesión cerrada: el backend del CRM respondió 401 en', url)
          localStorage.removeItem('crm_token')
          localStorage.removeItem('crm_user')
          setToken(null)
          setUser(null)
          router.push('/login?expired=1')
        } else if (!isOwnApiUrl(url)) {
          console.warn('[auth] 401 de un servicio externo, ignorado (no cierra la sesión):', url)
        }
      }

      return response
    }

    return () => { window.fetch = originalFetch }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router])

  return (
    <AuthContext.Provider value={{ user, token, loading, login, logout }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth debe usarse dentro de AuthProvider')
  return ctx
}
